import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Line } from '@ant-design/charts';
import { metricName } from './presentation';
import {
  capabilityChangeText,
  capabilityConfidenceText,
  capabilityQualityText,
  capabilityScoreText,
  capabilitySourceText,
  textOrDash,
  timelineDetailRows,
  timelinePresentation,
  timelineSourceText,
  timelineSummary,
  timeText,
} from './SimpleWorkspaceSupport';
import styles from './SimpleWorkspace.less';

const { Paragraph } = Typography;
const drawerAxis = {
  label: { style: { fill: '#64748b' } },
  line: { style: { stroke: '#d7e0e8' } },
  tickLine: { style: { stroke: '#d7e0e8' } },
  grid: { line: { style: { stroke: '#e8edf2', lineDash: [3, 3] } } },
};

export default function SimpleWorkspaceDrawers({ model, actions, columns }) {
  const {
    actionNames,
    actionProfileVisible,
    selectedActionProfile,
    actionMetricLoading,
    selectedMetricSeries,
    actionMetricChart,
    actionMetricTrend,
    capabilityHistoryVisible,
    capabilityHistoryTitle,
    capabilityHistoryChart,
    capabilityHistoryLoading,
    capabilityHistory,
    timelineDetailVisible,
    selectedTimelineEvent,
    timelineDetailLoading,
    timelineDetail,
  } = model;
  const detailRows = timelineDetailRows(timelineDetail);
  const latestCapability = capabilityHistory
    .slice()
    .sort(
      (left, right) =>
        Number(right.windowEndedAt || right.ctime || 0) -
        Number(left.windowEndedAt || left.ctime || 0),
    )[0];

  return (
    <>
      <Drawer
        width={980}
        className={styles.workspaceDrawer}
        visible={actionProfileVisible}
        title={`${
          actionNames[selectedActionProfile?.actionId] ||
          (selectedActionProfile?.actionId
            ? `动作 ${selectedActionProfile.actionId}`
            : '动作')
        } · 动作画像`}
        onClose={actions.closeActionProfile}
      >
        {selectedActionProfile && (
          <>
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 2, lg: 4 }}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="累计训练记录">
                {selectedActionProfile.trainingCount || 0} 次
              </Descriptions.Item>
              <Descriptions.Item label="动作评估">
                {selectedActionProfile.evaluationCount || 0} 次
              </Descriptions.Item>
              <Descriptions.Item label="已完成评估">
                {selectedActionProfile.succeededCount || 0} 次
              </Descriptions.Item>
              <Descriptions.Item label="同口径可比较">
                {selectedActionProfile.comparableEvaluationCount || 0} 次
              </Descriptions.Item>
            </Descriptions>
            <Card
              size="small"
              title="训练偏好与执行"
              style={{ marginBottom: 16 }}
            >
              <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
                <Descriptions.Item label="课表安排">
                  {selectedActionProfile.scheduledCount || 0} 次
                </Descriptions.Item>
                <Descriptions.Item label="实际完成">
                  {selectedActionProfile.completedCount || 0} 次
                </Descriptions.Item>
                <Descriptions.Item label="主动跳过">
                  {selectedActionProfile.skippedCount || 0} 次
                </Descriptions.Item>
                <Descriptions.Item label="明确喜欢">
                  <Tag color="green">
                    {selectedActionProfile.explicitLikeCount || 0} 次
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="明确不喜欢">
                  <Tag color="red">
                    {selectedActionProfile.explicitDislikeCount || 0} 次
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="问题观察">
                  {selectedActionProfile.problemObservationCount || 0} 次
                </Descriptions.Item>
              </Descriptions>
            </Card>
            <Paragraph type="secondary">
              喜欢与不喜欢来自课后反馈，跳过与完成来自课程动作快照；下方仅比较最近
              20 次中口径一致且至少有两条记录的客观指标。
              首末差值只描述数值变化，不直接代表进步或退步。
            </Paragraph>
          </>
        )}
        <Spin spinning={actionMetricLoading}>
          {selectedMetricSeries && actionMetricChart.length >= 2 && (
            <Card
              size="small"
              title={`${metricName(selectedMetricSeries)}变化`}
              style={{ marginBottom: 16 }}
            >
              <Line
                data={actionMetricChart}
                xField="date"
                yField="value"
                smooth
                point={{ size: 4 }}
                height={240}
                xAxis={drawerAxis}
                yAxis={drawerAxis}
              />
            </Card>
          )}
          {actionMetricTrend?.versionExcludedCount > 0 && (
            <Paragraph type="secondary">
              另有 {actionMetricTrend.versionExcludedCount}{' '}
              次历史评估因统计口径不同未参与比较。
            </Paragraph>
          )}
          <Table
            rowKey={(record) => `${record.metricCode}-${record.scope}`}
            dataSource={actionMetricTrend?.metricSeries || []}
            columns={columns.actionMetricColumns}
            pagination={{ pageSize: 10 }}
            locale={{
              emptyText: actionMetricLoading
                ? '正在读取动作指标'
                : '暂没有至少两次同口径的动作指标',
            }}
          />
        </Spin>
      </Drawer>

      <Drawer
        width={900}
        className={styles.workspaceDrawer}
        visible={capabilityHistoryVisible}
        title={capabilityHistoryTitle}
        onClose={actions.closeCapabilityHistory}
      >
        {latestCapability && (
          <>
            <div className={styles.capabilityHistorySummary}>
              <div>
                <span>当前能力</span>
                <strong>{capabilityScoreText(latestCapability)} / 10</strong>
              </div>
              <div>
                <span>训练确认</span>
                <strong>
                  {latestCapability.confirmedScore === null ||
                  latestCapability.confirmedScore === undefined
                    ? '待积累'
                    : `${(
                        Math.round(
                          (Number(latestCapability.confirmedScore) / 10) * 10,
                        ) / 10
                      ).toFixed(1)} / 10`}
                </strong>
              </div>
              <div>
                <span>最近变化</span>
                <strong>{capabilityChangeText(latestCapability)}</strong>
              </div>
              <div>
                <span>判断依据</span>
                <strong>
                  {capabilitySourceText(latestCapability.scoreSource)}
                </strong>
              </div>
              <div>
                <span>证据支撑</span>
                <strong>
                  {Number(latestCapability.relevantCourseCount) || 0} 节课 ·{' '}
                  {Number(latestCapability.distinctActionCount) || 0} 个动作
                </strong>
                <small>
                  {Number(latestCapability.independentWeekCount) || 0} 个训练周
                  · 跨度 {Number(latestCapability.trainingSpanDays) || 0} 天 ·{' '}
                  {Number(latestCapability.evidenceCount) || 0} 条证据 ·{' '}
                  {capabilityConfidenceText(latestCapability.confidenceLevel)}
                </small>
              </div>
            </div>
            <div className={styles.capabilityQualityStrip}>
              <span>
                覆盖度 <b>{capabilityQualityText(latestCapability.coverage)}</b>
              </span>
              <span>
                一致性{' '}
                <b>{capabilityQualityText(latestCapability.consistency)}</b>
              </span>
              <span>
                新鲜度{' '}
                <b>{capabilityQualityText(latestCapability.freshness)}</b>
              </span>
              <span>
                综合可信度{' '}
                <b>{capabilityQualityText(latestCapability.confidence)}</b>
              </span>
              <span>
                变化门槛{' '}
                <b>
                  {latestCapability.minimalDetectableChange === null ||
                  latestCapability.minimalDetectableChange === undefined
                    ? '-'
                    : `${
                        Number(latestCapability.minimalDetectableChange) / 10
                      } 分`}
                </b>
              </span>
            </div>
          </>
        )}
        {capabilityHistoryChart.length > 0 && (
          <Card size="small" title="能力得分变化" style={{ marginBottom: 16 }}>
            <Line
              data={capabilityHistoryChart}
              xField="date"
              yField="score"
              seriesField="series"
              color={['#2d83b8', '#31a36b']}
              smooth
              point={{ size: 4 }}
              xAxis={drawerAxis}
              yAxis={{ ...drawerAxis, min: 0, max: 10, tickCount: 11 }}
              height={240}
            />
          </Card>
        )}
        <Paragraph type="secondary" className={styles.capabilityScaleNote}>
          蓝线是随正常训练更新的当前估计，绿线是跨课程、跨训练周验证后的训练确认分。
          AI 跟练与 XPlan 使用同一套标尺；VK
          与视频只增强测量精度，不是确认资格。
          小于最小可检测变化（MDC）的波动不会标记为能力提升。
        </Paragraph>
        <Table
          rowKey={(record) => record.snapshotId || record.ctime}
          loading={capabilityHistoryLoading}
          dataSource={capabilityHistory}
          columns={columns.capabilityHistoryColumns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 835 }}
          locale={{ emptyText: '暂无能力变化记录' }}
        />
      </Drawer>

      <Drawer
        width={720}
        className={styles.workspaceDrawer}
        visible={timelineDetailVisible}
        title={timelinePresentation(selectedTimelineEvent).title}
        onClose={actions.closeTimelineDetail}
      >
        <Spin spinning={timelineDetailLoading}>
          {timelineDetail ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="事件类型">
                  {timelinePresentation(timelineDetail).category}
                </Descriptions.Item>
                <Descriptions.Item label="发生时间">
                  {timeText(timelineDetail.occurredAt)}
                </Descriptions.Item>
                <Descriptions.Item label="数据状态">
                  {timelineDetail.qualityStatus === 'qualified'
                    ? '信息完整'
                    : timelineDetail.qualityStatus === 'partial'
                    ? '部分信息'
                    : '状态已记录'}
                </Descriptions.Item>
                <Descriptions.Item label="数据来源">
                  {timelineSourceText(timelineDetail.sourceService)}
                </Descriptions.Item>
              </Descriptions>

              <Card size="small" title="事件摘要">
                <Paragraph style={{ marginBottom: 0 }}>
                  {timelineSummary(timelineDetail)}
                </Paragraph>
              </Card>

              {detailRows.length > 0 && (
                <Card size="small" title="事件数据">
                  <Descriptions size="small" column={1}>
                    {detailRows.map((item) => (
                      <Descriptions.Item key={item.label} label={item.label}>
                        {textOrDash(item.value)}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              )}

              <Space>
                {timelineDetail.sourceLocator?.roomId && (
                  <Button
                    type="primary"
                    onClick={() =>
                      actions.openCourseDetail({
                        roomId: timelineDetail.sourceLocator.roomId,
                      })
                    }
                  >
                    查看课程详情
                  </Button>
                )}
                {timelineDetail.sourceLocator?.evaluationId && (
                  <Button
                    onClick={() =>
                      actions.openActionDetail({
                        evaluationId: timelineDetail.sourceLocator.evaluationId,
                      })
                    }
                  >
                    查看动作评估
                  </Button>
                )}
              </Space>
            </Space>
          ) : (
            <Empty description="暂无事件详情" />
          )}
        </Spin>
      </Drawer>
    </>
  );
}

SimpleWorkspaceDrawers.propTypes = {
  model: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  columns: PropTypes.object.isRequired,
};
